import { ApexOptions } from "apexcharts";
import { useEffect, useMemo, useState } from "react";
import { useThemePreferencesProperties } from "../pages/Manager/UseThemePreferences";

type Appearance = "light" | "dark";

/**
 * Escalas Radix usadas como paleta categórica, por ordem de aplicação. A
 * primeira segue a cor de acento da instituição; as restantes são tons fixos
 * escolhidos por serem distinguíveis entre si em ambas as aparências.
 */
const PALETTE_TOKENS = [
  "--accent-9",
  "--blue-9",
  "--jade-9",
  "--amber-9",
  "--violet-9",
  "--pink-9",
  "--cyan-9",
  "--orange-9",
];

const SEMANTIC_TOKENS = {
  info: "--blue-9",
  success: "--jade-9",
  warning: "--amber-9",
  danger: "--red-9",
  neutral: "--gray-9",
};

/** Valores usados enquanto o tema não estiver montado no DOM. */
const FALLBACKS: Record<string, string> = {
  "--accent-9": "#29A383",
  "--blue-9": "#0090FF",
  "--jade-9": "#29A383",
  "--amber-9": "#FFC53D",
  "--violet-9": "#6E56CF",
  "--pink-9": "#D6409F",
  "--cyan-9": "#00A2C7",
  "--orange-9": "#F76B15",
  "--red-9": "#E5484D",
  "--gray-9": "#8D8D8D",
  "--gray-6": "#DDDDDD",
  "--gray-11": "#646464",
};

/**
 * O Radix define as suas variáveis no elemento `.radix-themes`, não no `body`.
 * Ler do sítio errado devolve string vazia e o gráfico fica sem cor.
 */
const themeRoot = (): Element | null =>
  typeof document === "undefined"
    ? null
    : document.querySelector(".radix-themes") ?? document.body;

const readToken = (root: Element | null, token: string): string => {
  if (!root) return FALLBACKS[token] ?? "";
  const value = getComputedStyle(root).getPropertyValue(token).trim();
  return value || FALLBACKS[token] || "";
};

export interface ChartTheme {
  appearance: Appearance;
  /** Paleta categórica já resolvida para valores concretos. */
  palette: string[];
  /** Cores por significado, para séries que representam estados. */
  semantic: Record<keyof typeof SEMANTIC_TOKENS, string>;
  /** Opções a espalhar em cada gráfico antes das opções específicas. */
  baseOptions: ApexOptions;
  /**
   * Funde as opções do tema com as de um gráfico, preservando o que o gráfico
   * define. Usar no ponto de renderização: `options={withTheme(minhasOpcoes)}`.
   */
  withTheme: (options?: ApexOptions) => ApexOptions;
}

/**
 * Devolve as cores e opções base do ApexCharts alinhadas com o tema activo.
 *
 * O ApexCharts desenha SVG e calcula legendas e tooltips em JavaScript, pelo
 * que não resolve `var(--token)` de forma fiável. Por isso os tokens são aqui
 * convertidos em valores concretos e recalculados quando a aparência muda.
 */
export const useChartTheme = (): ChartTheme => {
  const { data: themeData } = useThemePreferencesProperties();
  const appearance: Appearance =
    themeData?.appearance === "dark" ? "dark" : "light";

  const [tokens, setTokens] = useState<Record<string, string>>(FALLBACKS);

  useEffect(() => {
    const root = themeRoot();
    const wanted = [
      ...PALETTE_TOKENS,
      ...Object.values(SEMANTIC_TOKENS),
      "--gray-6",
      "--gray-11",
    ];

    const resolved: Record<string, string> = {};
    wanted.forEach((token) => {
      resolved[token] = readToken(root, token);
    });
    setTokens(resolved);
  }, [appearance]);

  return useMemo(() => {
    const foreColor = tokens["--gray-11"] ?? FALLBACKS["--gray-11"];
    const borderColor = tokens["--gray-6"] ?? FALLBACKS["--gray-6"];

    const semantic = Object.entries(SEMANTIC_TOKENS).reduce(
      (acc, [key, token]) => ({ ...acc, [key]: tokens[token] ?? FALLBACKS[token] }),
      {} as Record<keyof typeof SEMANTIC_TOKENS, string>
    );

    const baseOptions: ApexOptions = {
      chart: {
        toolbar: { show: false },
        fontFamily: "inherit",
        foreColor,
        background: "transparent",
        animations: { enabled: true, speed: 350 },
      },
      theme: { mode: appearance },
      grid: {
        borderColor,
        strokeDashArray: 4,
        xaxis: { lines: { show: false } },
      },
      tooltip: { theme: appearance },
      dataLabels: { enabled: false },
      legend: { labels: { colors: foreColor } },
      stroke: { width: 2 },
    };

    const palette = PALETTE_TOKENS.map((token) => tokens[token] ?? FALLBACKS[token]);

    /**
     * Fusão rasa por secção: o gráfico manda no que define, o tema preenche o
     * resto. As secções são fundidas uma a uma porque um spread simples
     * substituiria `chart` inteiro e perderia o `foreColor`.
     */
    const withTheme = (options: ApexOptions = {}): ApexOptions => ({
      ...baseOptions,
      ...options,
      colors: options.colors ?? palette,
      chart: { ...baseOptions.chart, ...options.chart },
      grid: { ...baseOptions.grid, ...options.grid },
      legend: { ...baseOptions.legend, ...options.legend },
      tooltip: { ...baseOptions.tooltip, ...options.tooltip },
      theme: { ...baseOptions.theme, ...options.theme },
    });

    return { appearance, palette, semantic, baseOptions, withTheme };
  }, [tokens, appearance]);
};

export default useChartTheme;
